import { useModalState } from '@vtex/admin-ui'
import { useWatch } from '@vtex/admin-ui-form'
import { useAdmin } from '@vtex/raccoon-next'
import {
  BudgetActions,
  BudgetPermissions,
  useBudgetContext,
  useBudgetQuery,
  useErrorHandler,
  useFeature,
} from 'hooks'
import { useEffect, useMemo } from 'react'
import { catchLogger } from 'shared'
import { Event, type BaseBudget, type Budget as BudgetType } from 'typings'
import { useAnalytics } from 'use-analytics'
import { useFormStateContext } from '../../../providers'
import {
  FormAliasKeys,
  useAddFormAlias,
} from '../../../providers/useFormAlias.constate'
import { useDrawerState } from '../../Drawer'
import { allowedDiscountTypes } from './constants'
import { BudgetEditor } from './Editor'
import { parseBudget } from './parseBudget'
import { trackBudgetChange } from './trackBudget'
import { BudgetViewRenderer } from './View/BudgetViewRenderer'
import { BudgetDeleteModal } from './View/components/DeleteModal'
import { BudgetViewState, useBudgetViewState } from './View/useBudgetViewState'

type Props = {
  name: string
}

export function Budget(props: Props) {
  const { name } = props

  const { form, discountType } = useFormStateContext()
  const editorState = useDrawerState()
  const admin = useAdmin()

  const budget: BudgetType | undefined = useWatch({ form, name })
  const { baseBudget, budgetIds, isComplete } = parseBudget(budget)

  const isAllowedDiscountType = useMemo(
    () => !discountType || allowedDiscountTypes.includes(discountType),
    [discountType]
  )

  const {
    data: budgetQuery,
    error,
    isLoading,
  } = useBudgetQuery({
    id: budgetIds?.id ?? '',
    allocationId: budgetIds?.allocationId ?? '',
    options: {
      enabled: !!budgetIds?.id,
    },
  })

  const viewState = useBudgetViewState({
    baseBudget,
    budgetIds,
    isBudgetComplete: isComplete,
    isAllowedDiscountType,
    isLoading,
  })

  const budgetContext = useBudgetContext()
  const { setOriginalValue, setAction, setPermission } = budgetContext

  const deleteModalState = useModalState()
  const { getFeature } = useFeature()
  const budgetFeature = getFeature('budget')
  const { addAlias } = useAddFormAlias()
  const { track } = useAnalytics()

  useEffect(() => {
    addAlias({ value: name, key: FormAliasKeys.budgetRestrictions })
  }, [addAlias, name])

  useErrorHandler(error, {
    onNotFound: () => {
      form.setValue(name, undefined)
    },
    onForbidden: () => {
      setPermission(BudgetPermissions.NONE)
      trackBudgetChange({
        event: Event.BUDGET_PERMISSION_ERROR,
        injectedPayload: { permission: BudgetPermissions.NONE },
        formValues: form.getValues(),
        budgetContext,
        discountType,
        track,
      })
    },
    onUnknownError: (err) => {
      catchLogger({
        id: 'fetch-budget',
        fn: () => {
          throw new Error(`Error fetching budget: ${err.message}`, {
            cause: err,
          })
        },
        admin,
      })
    },
  })

  useEffect(() => {
    if (budgetQuery) {
      setOriginalValue(budgetQuery)
    }
  }, [budgetQuery, setOriginalValue])

  useEffect(() => {
    if (budgetQuery) {
      const formBudget = form.getValues(name)
      form.setValue(name, { ...budgetQuery, ...formBudget })
    }
  }, [budgetQuery, form, name])

  if (!budgetFeature || viewState === BudgetViewState.INVALID) {
    return null
  }

  const handleRemove = () => deleteModalState.show()

  const removeBudget = () => {
    form.setValue(name, undefined)
    setAction(BudgetActions.DELETE)
  }

  const handleSave = (config: BaseBudget) => {
    const valueToSave = budgetIds ? { ...budgetIds, ...config } : config
    form.setValue(name, valueToSave)
    setAction(budgetIds ? BudgetActions.UPDATE : BudgetActions.CREATE)
  }

  return (
    <>
      <BudgetViewRenderer
        viewState={viewState}
        budget={baseBudget}
        isAllowedDiscountType={isAllowedDiscountType}
        onAdd={editorState.show}
        onRemove={handleRemove}
      />
      <BudgetEditor
        initialConfig={baseBudget}
        state={editorState}
        onSave={handleSave}
      />
      <BudgetDeleteModal
        state={deleteModalState}
        onCancel={deleteModalState.hide}
        onConfirm={removeBudget}
      />
    </>
  )
}
